import { studyQueryKeys } from '@/config/queryKeys';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import { PaginatedSampleProteoformCountsResponse, SampleProteoformCountPublic } from '@/types';
import { Badge } from '@nautilusbio/nb-design-system/Badge';
import { Button } from '@nautilusbio/nb-design-system/Button';
import Loading from '@nautilusbio/nb-design-system/Loading';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { ColDef, GridOptions, type RowClickedEvent } from 'ag-grid-enterprise';
import type { AgGridReact } from 'ag-grid-react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { type RefObject, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PanelImperativeHandle } from 'react-resizable-panels';

import tableStyles from '../../../styles/components/table.module.css';
import irStyles from '../../Instrument_Runs/styles.module.css';
import styles from '../styles.module.css';
import { createSetFilterParams, createSharedSampleColumns } from '../utils/sharedColumnDefs';

import { ProteoformDetailRenderer } from './ProteoformDetailRenderer';
import { useSampleTableGrid } from './useSampleTableGrid';

const handleRowClicked = (event: RowClickedEvent<SampleProteoformCountPublic>) => {
  const node = event.node;

  if (node) {
    node.setExpanded(!node.expanded);
    // Redraw the row to apply CSS class immediately
    event.api.redrawRows({ rowNodes: [node] });
  }
};

interface SampleProteoformCountsTableProps {
  adjacentPanelRef: RefObject<PanelImperativeHandle | null>;
  orgId: string;
  panelRef: RefObject<PanelImperativeHandle | null>;
  studyId: string;
}

const localStorageKey = (studyId: string) => `study-${studyId}-proteoformCounts-agGridState`;
const paginationPageSizeKey = (studyId: string) => `study-${studyId}-proteoformCounts-pageSize`;

// Valid sort fields for this endpoint
const VALID_SORT_FIELDS = ['sample_name', 'instrument_run_name', 'num_proteoforms', 'well_position', 'demux_index'];

// Available page size options for user selection via AG Grid's built-in controls
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

/**
 * Grid options with server-side filtering, sorting, and compact layout
 * Uses AG Grid's built-in pagination size selector
 */
const gridOptions: GridOptions = {
  cacheBlockSize: 100,
  maxBlocksInCache: 10,
  suppressMovableColumns: false,
  suppressDragLeaveHidesColumns: false,
};

/**
 * SampleProteoformCountsTable Component
 *
 * Displays sample proteoform count data with server-side filtering, sorting, and pagination.
 * Features:
 * - Column header menu filters (no floating filter row for clean UI)
 * - Set Filters for categorical columns (instrument_run_name, well_position, demux_index)
 * - Text filter for free-form data (sample_name)
 * - Number filter for numeric data (num_proteoforms)
 * - Natural sorting for sample names with numeric components
 * - User-selectable rows per page via AG Grid's built-in pagination controls
 * - Fixed height scrollable container for consistent layout
 * - Compact visual layout with reduced padding
 * - Column state persistence
 *
 * @param props - Component props
 * @returns Rendered table with enhanced AG Grid filtering and pagination
 */
export const SampleProteoformCountsTable = forwardRef(function SampleProteoformCountsTable(
  { adjacentPanelRef, studyId, orgId, panelRef }: SampleProteoformCountsTableProps,
  ref,
) {
  const { t } = useTranslation('studies');
  const gridRef = useRef<AgGridReact<SampleProteoformCountPublic>>(null);

  /**
   * Create AG Grid server-side datasource with enhanced filter handling
   * Maps AG Grid filter model to API query parameters
   */
  const datasource = useMemo(
    () =>
      createAgGridDatasource<SampleProteoformCountPublic, PaginatedSampleProteoformCountsResponse>(
        studyQueryKeys.endpoints.proteoformCountsSearch(orgId, studyId),
        ({ skip, limit, sortBy, sortOrder, filters }) => {
          // Ensure we only use valid sort fields
          const validSortBy = sortBy && VALID_SORT_FIELDS.includes(sortBy) ? sortBy : 'sample_name';
          return {
            skip,
            limit,
            sort_by: validSortBy,
            sort_order: (sortOrder || 'asc') as 'asc' | 'desc',
            // Pass filters directly if they exist, otherwise null
            filters: Object.keys(filters).length > 0 ? filters : null,
          };
        },
      ),
    [orgId, studyId],
  );

  // Use shared hook for state and handlers
  const {
    isCollapsed,
    setIsCollapsed,
    pageSize,
    totalCount,
    handleTogglePanel,
    tableContainerClasses,
    defaultColDef,
    handleGridReady,
    handleFirstDataRendered,
    handleSortChanged,
    handleColumnVisible,
    handleFilterChanged,
    handleModelUpdated,
    handlePaginationChanged,
    handleGridSizeChanged,
  } = useSampleTableGrid<SampleProteoformCountPublic>({
    panelRef,
    adjacentPanelRef,
    studyId,
    validSortFields: VALID_SORT_FIELDS,
    localStorageKeyFn: localStorageKey,
    paginationPageSizeKeyFn: paginationPageSizeKey,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    datasource,
  });

  // Expose imperative method for parent to update collapsed state
  useImperativeHandle(ref, () => ({
    updateCollapsed: (collapsed: boolean) => setIsCollapsed(collapsed),
  }));

  /**
   * Build column definitions with enhanced filtering capabilities
   * Includes shared sample columns (with Set Filters for categorical data)
   * plus custom proteoform count column with Set Filter
   */
  const countColDefs: ColDef<SampleProteoformCountPublic>[] = useMemo(
    () => [
      ...createSharedSampleColumns<SampleProteoformCountPublic>(t),
      {
        cellClass: tableStyles.tableCellCenter,
        field: 'num_proteoforms',
        filter: 'agSetColumnFilter',
        filterParams: createSetFilterParams('num_proteoforms'),
        headerName: t('columnHeaders.proteoformCount'),
        resizable: true,
        sortable: true,
        valueFormatter: ({ value }) => value?.toLocaleString() || '0',
      },
    ],
    [t],
  );

  /**
   * Update datasource when it changes (dependencies update)
   */
  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('serverSideDatasource', datasource);
    }
  }, [datasource]);

  // Icon logic (unique to this component)
  const Icon = isCollapsed ? ChevronDown : ChevronUp;

  /**
   * Apply CSS class to expanded master rows for proper background styling
   * when the row becomes sticky during scrolling
   */
  const getRowClass = useCallback(
    (params: { node: { expanded?: boolean } }) => (params.node.expanded ? 'ag-row-master-expanded' : ''),
    [],
  );

  return (
    <Stack className={irStyles.proteoformsTableContainer} fullHeight fullWidth gap={4} orientation="vertical">
      <Stack
        alignItems="center"
        className={irStyles.proteoformsTitleRow}
        justifyContent="space-between"
        orientation="horizontal"
        onDoubleClick={e => {
          // Prevent double-click on the button from triggering the row handler
          if (
            e.target instanceof HTMLElement &&
            e.currentTarget.contains(e.target) &&
            !e.target.closest('button,[data-testid="toggle-expanded"]')
          ) {
            handleTogglePanel();
          }
        }}
      >
        <Stack gap={4} orientation="horizontal">
          <Text as="h3">{t('proteoformCounts.title')}</Text>
          <Badge size="sm" variant="info">
            {totalCount === null ? (
              <Loading className={styles.isRefetching} type="inline" />
            ) : (
              totalCount.toLocaleString()
            )}
          </Badge>
        </Stack>

        <Button
          aria-label={isCollapsed ? t('toggle.expand', { ns: 'sidebar' }) : t('toggle.collapse', { ns: 'sidebar' })}
          data-testid={'toggle-expanded'}
          size="sm"
          onPress={handleTogglePanel}
          visual="unstyled"
        >
          <Icon size={16} />
        </Button>
      </Stack>

      <Table
        columnDefs={countColDefs}
        container={tableContainerClasses}
        defaultColDef={defaultColDef}
        domLayout="normal"
        enableAdvancedFilter={false}
        enableResponsiveResize={true}
        gridOptions={gridOptions}
        onGridReady={handleGridReady}
        onFirstDataRendered={handleFirstDataRendered}
        onSortChanged={handleSortChanged}
        onColumnVisible={handleColumnVisible}
        onFilterChanged={handleFilterChanged}
        onModelUpdated={handleModelUpdated}
        onPaginationChanged={handlePaginationChanged}
        onGridSizeChanged={handleGridSizeChanged}
        pagination
        paginationPageSize={pageSize}
        paginationPageSizeSelector={PAGE_SIZE_OPTIONS}
        ref={gridRef}
        resizeDebounceMs={150}
        rowModelType="serverSide"
        masterDetail={true}
        detailRowAutoHeight={false}
        embedFullWidthRows={true}
        detailCellRenderer={(props: { data: SampleProteoformCountPublic }) => (
          <ProteoformDetailRenderer data={props.data} orgId={orgId} />
        )}
        getRowId={(params: { data: SampleProteoformCountPublic }) => params.data.sample_id}
        getRowClass={getRowClass}
        onRowClicked={handleRowClicked}
        tableId="sample-proteoform-counts-table"
      />
    </Stack>
  );
});
